/**
 * Settings page – token management, auto-renew, notifications, spending limits.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  RefreshCw,
  ShieldAlert,
  AlertCircle,
  Key,
  Copy,
  CheckCircle2,
  RotateCcw,
  Trash2,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { getTokenBalance, rotateToken, revokeGraceToken } from '../services';
import type { TokenBalance, TokenRotationResult } from '../types';

export default function SettingsPage() {
  const navigate = useNavigate();

  // ── Token state ────────────────────────────────────────────
  const [balance, setBalance] = useState<TokenBalance | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [copied, setCopied] = useState(false);

  // Rotation flow state
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotationResult, setRotationResult] = useState<TokenRotationResult | null>(null);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [newTokenCopied, setNewTokenCopied] = useState(false);

  // Revoke grace flow state
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const loadBalance = useCallback(async () => {
    setLoadingBalance(true);
    try {
      const res = await getTokenBalance();
      if (res.data) setBalance(res.data);
    } finally {
      setLoadingBalance(false);
    }
  }, []);

  useEffect(() => { loadBalance(); }, [loadBalance]);

  const handleCopyToken = async () => {
    if (!balance?.userToken) return;
    await navigator.clipboard.writeText(balance.userToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyNewToken = async () => {
    if (!rotationResult?.newToken) return;
    await navigator.clipboard.writeText(rotationResult.newToken);
    setNewTokenCopied(true);
    setTimeout(() => setNewTokenCopied(false), 2000);
  };

  const handleRotate = async () => {
    setRotating(true);
    setRotateError(null);
    try {
      const res = await rotateToken();
      if (res.success && res.data) {
        setRotationResult(res.data);
        setShowRotateConfirm(false);
        await loadBalance();
      } else {
        setRotateError(res.error || 'Failed to rotate token.');
        setShowRotateConfirm(false);
      }
    } finally {
      setRotating(false);
    }
  };

  const handleRevokeGrace = async () => {
    setRevoking(true);
    setRevokeError(null);
    try {
      const res = await revokeGraceToken();
      if (res.success) {
        setShowRevokeConfirm(false);
        await loadBalance();
      } else {
        setRevokeError(res.error || 'Failed to revoke old token.');
        setShowRevokeConfirm(false);
      }
    } finally {
      setRevoking(false);
    }
  };

  const formatGraceExpiry = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const maskedToken = balance?.userToken
    ? balance.userToken.length > 16
      ? `${balance.userToken.substring(0, 12)}••••••••`
      : balance.userToken
    : '••••••••••••';

  const hasGraceToken = !!balance?.graceToken;

  return (
    <div className="page-container">
      <div className="container container-md">
        <div className="page-header">
          <div>
            <h1>Settings</h1>
            <p>Manage your account preferences</p>
          </div>
          <button className="btn btn-ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={18} />
            Dashboard
          </button>
        </div>

        {/* ── User Token ─────────────────────────────────────────── */}
        <div className="card settings-card">
          <div className="card-header">
            <Key size={20} />
            <h3>User Token</h3>
          </div>
          <div className="card-body">

            {/* New token revealed after rotation */}
            {rotationResult && (
              <div className="alert alert-success" style={{ marginBottom: 16 }}>
                <CheckCircle2 size={16} />
                <div style={{ flex: 1 }}>
                  <strong>New token created.</strong> Copy it now — it won't be shown again.
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <code style={{ flex: 1, wordBreak: 'break-all', fontSize: 13 }}>
                      {rotationResult.newToken}
                    </code>
                    <button className="btn btn-sm btn-ghost" onClick={handleCopyNewToken}>
                      {newTokenCopied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {rotateError && (
              <div className="alert alert-error" style={{ marginBottom: 16 }}>
                <AlertCircle size={16} />
                <span>{rotateError}</span>
              </div>
            )}

            {revokeError && (
              <div className="alert alert-error" style={{ marginBottom: 16 }}>
                <AlertCircle size={16} />
                <span>{revokeError}</span>
              </div>
            )}

            {/* Token preview + copy */}
            <div className="setting-row" style={{ alignItems: 'flex-start' }}>
              <div className="setting-info">
                <strong>Active Token</strong>
                <p>Used by connected apps to make API requests on your behalf.</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <code style={{ fontSize: 13 }}>
                    {loadingBalance ? '••••••••••••' : maskedToken}
                  </code>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={handleCopyToken}
                    disabled={loadingBalance || !balance?.userToken}
                    title="Copy token"
                  >
                    {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Regenerate */}
            {!showRotateConfirm ? (
              <div className="setting-row">
                <div className="setting-info">
                  <strong>Regenerate Token</strong>
                  <p>
                    Create a new token. Your current token stays valid for 24 hours so you can update connected apps.
                  </p>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setShowRotateConfirm(true); setRotationResult(null); }}
                  disabled={loadingBalance || rotating || hasGraceToken}
                  title={hasGraceToken ? 'Revoke the grace-period token before rotating again' : undefined}
                >
                  <RotateCcw size={16} />
                  Regenerate
                </button>
              </div>
            ) : (
              <div className="alert alert-warning" style={{ marginBottom: 0 }}>
                <AlertTriangle size={16} />
                <div style={{ flex: 1 }}>
                  <strong>Regenerate your token?</strong>
                  <p style={{ margin: '4px 0 12px' }}>
                    Your current token will keep working for <strong>24 hours</strong>.
                    After that, only the new token will be accepted.
                    Update all connected apps within this window.
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={handleRotate}
                      disabled={rotating}
                    >
                      {rotating ? 'Regenerating…' : 'Yes, Regenerate'}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowRotateConfirm(false)}
                      disabled={rotating}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Grace-period token status */}
            {hasGraceToken && balance?.graceToken && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <div className="setting-row">
                  <div className="setting-info">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <Clock size={14} style={{ color: 'var(--warning)' }} />
                      <strong style={{ color: 'var(--warning)' }}>Old Token — Grace Period</strong>
                    </div>
                    <p>
                      <code style={{ fontSize: 13 }}>{balance.graceToken.tokenPrefix}••••••••</code>
                      {' '}remains valid until{' '}
                      <strong>{formatGraceExpiry(balance.graceToken.graceEndsAt)}</strong>.
                      Update all connected apps before then.
                    </p>
                  </div>
                  {!showRevokeConfirm ? (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => setShowRevokeConfirm(true)}
                      disabled={revoking}
                    >
                      <Trash2 size={14} />
                      Revoke Now
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={handleRevokeGrace}
                        disabled={revoking}
                      >
                        {revoking ? 'Revoking…' : 'Confirm Revoke'}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setShowRevokeConfirm(false)}
                        disabled={revoking}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Auto-Renew (coming soon) ───────────────────────────── */}
        <div className="card settings-card" style={{ opacity: 0.5, pointerEvents: 'none' }}>
          <div className="card-header">
            <RefreshCw size={20} />
            <h3>Auto-Renew <span className="coming-soon-tag">Coming soon</span></h3>
          </div>
          <div className="card-body">
            <div className="setting-row">
              <div className="setting-info">
                <strong>Enable Auto-Renew</strong>
                <p>Automatically purchase tokens when your balance falls below a threshold.</p>
              </div>
              <label className="toggle">
                <input type="checkbox" checked={false} disabled readOnly />
                <span className="toggle-slider" />
              </label>
            </div>
          </div>
        </div>

        {/* ── Spending Limits (coming soon) ─────────────────────── */}
        <div className="card settings-card" style={{ opacity: 0.5, pointerEvents: 'none' }}>
          <div className="card-header">
            <ShieldAlert size={20} />
            <h3>Spending Limits <span className="coming-soon-tag">Coming soon</span></h3>
          </div>
          <div className="card-body">
            <div className="setting-row">
              <div className="setting-info">
                <strong>Daily Spending Limit</strong>
                <p>Maximum tokens that can be used per day.</p>
              </div>
              <input
                type="number"
                className="form-input sm"
                defaultValue={0}
                disabled
                placeholder="No limit"
              />
            </div>
          </div>
        </div>

        {/* ── Notifications (coming soon) ───────────────────────── */}
        <div className="card settings-card" style={{ opacity: 0.5, pointerEvents: 'none' }}>
          <div className="card-header">
            <Bell size={20} />
            <h3>Notifications <span className="coming-soon-tag">Coming soon</span></h3>
          </div>
          <div className="card-body">
            <div className="setting-row">
              <div className="setting-info">
                <strong>Email Notifications</strong>
                <p>Receive email for purchases, low balance alerts, and account activity.</p>
              </div>
              <label className="toggle">
                <input type="checkbox" checked={false} disabled readOnly />
                <span className="toggle-slider" />
              </label>
            </div>
            <div className="setting-row">
              <div className="setting-info">
                <strong>Low Balance Alert</strong>
                <p>Get notified when your balance drops below a set amount.</p>
              </div>
              <input
                type="number"
                className="form-input sm"
                defaultValue={0}
                disabled
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

